import React from 'react'
import { Alert, Box, Stack, Typography, useTheme } from '@mui/material'
import { CloudUpload } from '@mui/icons-material'
import FileUploadField from '@app/src/components/FileUploadField'
import EmptyState from '@app/src/components/EmptyState/EmptyState'
import Avatar, { AvatarSize } from '@app/src/components/Ui/Avatar'
import LoadingButton from '@app/src/components/LoadingButton'
import MorphingLoader from '@app/src/components/MorphingLoader'
import UnfinishedImportsList from './UnfinishedImportsList'
import { ACCEPTED_FILE_TYPES } from '@app/src/types/tabularImport'

export type UploadStepProps = {
  isAnalyzing: boolean
  importFile: string | Blob
  fileError: string | null
  isUploading: boolean
  sessionId: string | null
  uploadDescription: string
  onFileChange: React.Dispatch<React.SetStateAction<string | Blob>>
  onFileRejected: (rejections: { file: File; errors: { code: string; message: string }[] }[]) => void
  onUpload: () => void
  onResumeSession: (sessionId: string) => void
}

const UploadStep: React.FC<UploadStepProps> = ({
  isAnalyzing,
  importFile,
  fileError,
  isUploading,
  sessionId,
  uploadDescription,
  onFileChange,
  onFileRejected,
  onUpload,
  onResumeSession,
}) => {
  const { palette } = useTheme()

  if (isAnalyzing) {
    return (
      <Stack spacing={4} alignItems="center">
        <Avatar variant="circular" size={AvatarSize.XXXL} sx={{ bgcolor: 'communication.light' }}>
          <MorphingLoader size={56} color={palette.communication.dark} transitionDuration={400} />
        </Avatar>
        <Typography variant="h5">Analyzing your file...</Typography>
        <Box width="100%" maxWidth={600} textAlign="center">
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Our AI is analyzing your file to automatically detect and map columns to Worldfavor fields. This usually
            takes a few moments.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Please wait while we process your file...
          </Typography>
        </Box>
      </Stack>
    )
  }

  return (
    <Stack spacing={4}>
      {/* Start New Import section - show first */}
      <Box>
        <Typography variant="h6" gutterBottom>
          Start new import
        </Typography>
        <Box
          sx={{
            backgroundColor: 'grey.100',
            border: '2px dashed',
            borderColor: 'grey.400',
            borderRadius: 2,
            p: 4,
          }}
        >
          <EmptyState iconComponent={CloudUpload} title="Upload a file" description={uploadDescription}>
            <FileUploadField setFile={onFileChange} accept={ACCEPTED_FILE_TYPES} onRejected={onFileRejected} />
          </EmptyState>
          {fileError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {fileError}
            </Alert>
          )}
        </Box>
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <LoadingButton
            loading={isUploading}
            variant="contained"
            onClick={onUpload}
            disabled={!importFile}
            startIcon={<CloudUpload />}
          >
            Upload & analyze
          </LoadingButton>
        </Box>
      </Box>

      {/* Show unfinished imports if user hasn't started a new upload yet */}
      {!sessionId && <UnfinishedImportsList onSelectSession={onResumeSession} />}
    </Stack>
  )
}

export default UploadStep
